<template>
  <div
    v-if="show && receipt"
    class="fixed inset-0 z-[1200] flex items-center justify-center p-3 sm:p-4 bg-slate-900/70 backdrop-blur-sm overflow-y-auto"
  >
    <!-- Modal Container -->
    <div
      class="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden my-6 flex flex-col max-h-[90vh]"
    >
      <!-- Top Action Bar (hidden in print) -->
      <div
        class="px-6 py-4 bg-slate-900 text-white flex items-center justify-between no-print flex-shrink-0"
      >
        <div class="flex items-center space-x-2.5">
          <Receipt class="w-5 h-5 text-emerald-400" />
          <span class="text-sm font-bold tracking-wide">Official Fee Receipt</span>
          <span
            class="ml-2 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
          >
            {{ receipt.receiptNumber }}
          </span>
        </div>
        <div class="flex items-center space-x-2">
          <button
            @click="handlePrint"
            class="px-3.5 py-1.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow transition flex items-center gap-1.5"
          >
            <Printer class="w-3.5 h-3.5" />
            Print Receipt
          </button>
          <button
            @click="$emit('close')"
            class="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition"
          >
            <X class="w-5 h-5" />
          </button>
        </div>
      </div>

      <!-- Printable Receipt Content Area -->
      <div
        id="printable-receipt"
        class="flex-1 overflow-y-auto p-6 sm:p-8 bg-white text-slate-800 space-y-6 print:p-0 print:overflow-visible"
      >
        <!-- School Header Banner -->
        <div
          class="flex flex-col sm:flex-row items-center sm:items-start justify-between pb-6 border-b-2 border-slate-900 gap-4"
        >
          <div class="flex items-center space-x-4 text-center sm:text-left">
            <div
              v-if="schoolInfo.logo"
              class="w-16 h-16 rounded-xl border border-slate-200 overflow-hidden flex-shrink-0"
            >
              <img
                :src="schoolInfo.logo"
                alt="School Logo"
                class="w-full h-full object-contain"
              />
            </div>
            <div
              v-else
              class="w-16 h-16 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center text-white shadow-md flex-shrink-0"
            >
              <GraduationCap class="w-8 h-8" />
            </div>

            <div>
              <h1 class="text-xl sm:text-2xl font-black text-slate-900 uppercase tracking-tight">
                {{ schoolInfo.schoolName || "Educanium School" }}
              </h1>
              <p v-if="schoolInfo.tagline" class="text-xs text-indigo-700 font-medium">
                {{ schoolInfo.tagline }}
              </p>
              <p v-if="schoolInfo.address" class="text-xs text-slate-500 mt-1">
                {{ schoolInfo.address }}
              </p>
              <div class="flex flex-wrap items-center gap-x-3 text-xs text-slate-500 mt-0.5">
                <span v-if="schoolInfo.primaryPhone">Ph: {{ schoolInfo.primaryPhone }}</span>
                <span v-if="schoolInfo.email">Email: {{ schoolInfo.email }}</span>
                <span v-if="schoolInfo.schoolCode" class="font-semibold text-slate-700"
                  >Code: {{ schoolInfo.schoolCode }}</span
                >
              </div>
            </div>
          </div>

          <!-- Receipt Stamp & Badge -->
          <div class="text-center sm:text-right flex-shrink-0">
            <span
              class="inline-block px-3 py-1 bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-md text-xs font-black uppercase tracking-wider"
            >
              PAYMENT ACKNOWLEDGEMENT
            </span>
            <div class="mt-2 text-xs text-slate-600">
              <span class="font-bold text-slate-900">Receipt No: </span>
              <span class="font-mono font-bold text-indigo-700">{{ receipt.receiptNumber }}</span>
            </div>
            <div class="text-xs text-slate-600">
              <span class="font-bold text-slate-900">Date: </span>
              <span>{{ formatDate(receipt.paymentDate) }}</span>
            </div>
          </div>
        </div>

        <!-- Student Details Grid -->
        <div class="bg-slate-50 p-4 rounded-xl border border-slate-200 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <div>
            <span class="text-slate-400 font-semibold block uppercase text-[10px]">Student Name</span>
            <span class="font-bold text-slate-900 text-sm block truncate">
              {{ receipt.studentId?.name || receipt.studentName || "N/A" }}
            </span>
          </div>
          <div>
            <span class="text-slate-400 font-semibold block uppercase text-[10px]">Student ID / Roll</span>
            <span class="font-bold text-slate-900 block">
              {{ receipt.studentId?.rollNumber || receipt.studentRollNumber || "N/A" }}
            </span>
          </div>
          <div>
            <span class="text-slate-400 font-semibold block uppercase text-[10px]">Class & Section</span>
            <span class="font-bold text-slate-900 block">
              {{ formatClass(receipt.studentId?.class) }}
            </span>
          </div>
          <div>
            <span class="text-slate-400 font-semibold block uppercase text-[10px]">Academic Session</span>
            <span class="font-bold text-slate-900 block">
              {{ receipt.academicSession || receipt.studentId?.session || "N/A" }}
            </span>
          </div>
        </div>

        <!-- Payment Particulars Table -->
        <div class="border border-slate-200 rounded-xl overflow-hidden">
          <table class="w-full text-left text-xs">
            <thead class="bg-slate-100 text-slate-700 uppercase font-bold text-[11px] border-b border-slate-200">
              <tr>
                <th class="py-2.5 px-4">#</th>
                <th class="py-2.5 px-4">Fee Description</th>
                <th class="py-2.5 px-4">Billing Period</th>
                <th class="py-2.5 px-4 text-right">Previous Due</th>
                <th class="py-2.5 px-4 text-right">Paid Amount</th>
                <th class="py-2.5 px-4 text-right">Remaining Balance</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 font-medium">
              <tr>
                <td class="py-3 px-4 font-mono text-slate-400">01</td>
                <td class="py-3 px-4">
                  <div class="font-bold text-slate-900 text-sm">
                    {{ receipt.feeCategory === "admission" ? "One-Time Admission Fee" : "Recurring Monthly Tuition Fee" }}
                  </div>
                  <div v-if="receipt.remarks" class="text-[11px] text-slate-500 italic">
                    Note: {{ receipt.remarks }}
                  </div>
                </td>
                <td class="py-3 px-4 text-slate-700 font-semibold">
                  {{
                    receipt.feeCategory === "monthly" && receipt.monthName
                      ? `${receipt.monthName} ${receipt.year}`
                      : "One-Time"
                  }}
                </td>
                <td class="py-3 px-4 text-right text-slate-600 font-mono">
                  ₹{{ (receipt.previousBalance || 0).toLocaleString("en-IN") }}
                </td>
                <td class="py-3 px-4 text-right font-bold text-emerald-700 font-mono text-sm">
                  ₹{{ (receipt.amount || 0).toLocaleString("en-IN") }}
                </td>
                <td class="py-3 px-4 text-right font-bold text-slate-800 font-mono">
                  ₹{{ (receipt.remainingBalance || 0).toLocaleString("en-IN") }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Total Highlights & Payment Info -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center pt-2">
          <!-- Payment Method info -->
          <div class="bg-slate-50 p-3.5 rounded-xl border border-slate-200 text-xs space-y-1">
            <div class="flex items-center justify-between">
              <span class="text-slate-500">Payment Mode:</span>
              <span class="font-bold text-slate-800">{{ receipt.paymentMethod }}</span>
            </div>
            <div v-if="receipt.transactionReference" class="flex items-center justify-between">
              <span class="text-slate-500">Reference / UTR:</span>
              <span class="font-mono font-semibold text-slate-700">{{ receipt.transactionReference }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-slate-500">Collected By:</span>
              <span class="font-semibold text-slate-800">{{ receipt.recordedByName || "Administrator" }}</span>
            </div>
          </div>

          <!-- Total Amount Box -->
          <div class="bg-gradient-to-br from-emerald-500 to-teal-600 p-4 rounded-xl text-white shadow-md text-right">
            <span class="text-xs uppercase tracking-wider text-emerald-100 font-bold block">
              Total Amount Received
            </span>
            <span class="text-2xl sm:text-3xl font-black font-mono">
              ₹{{ (receipt.amount || 0).toLocaleString("en-IN") }}
            </span>
            <span class="block text-[11px] text-emerald-100 mt-1 capitalize font-medium">
              ({{ amountInWords(receipt.amount) }} Rupees Only)
            </span>
          </div>
        </div>

        <!-- Footer Signatures & Disclaimer -->
        <div class="pt-8 border-t border-dashed border-slate-300 grid grid-cols-2 gap-6 text-xs text-slate-500 items-end">
          <div>
            <p class="text-[11px]">
              • This is a computer-generated fee receipt.
            </p>
            <p class="text-[11px]">
              • Fees once paid are non-refundable according to school policy.
            </p>
          </div>
          <div class="text-right">
            <div class="inline-block border-t border-slate-800 pt-1 px-8 text-center">
              <span class="font-bold text-slate-800 block">Authorized Signatory</span>
              <span class="text-[10px] text-slate-500">{{ schoolInfo.schoolName || "Educanium" }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed } from "vue";
  import { Receipt, X, Printer, GraduationCap } from "lucide-vue-next";

  const props = defineProps({
    show: { type: Boolean, default: false },
    receipt: { type: Object, default: null },
  });

  defineEmits(["close"]);

  const schoolInfo = computed(() => {
    return props.receipt?.school || {};
  });

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const d = new Date(dateString);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatClass = (cls) => {
    if (!cls) return "N/A";
    if (typeof cls === "string") return cls;
    let out = cls.name || "N/A";
    if (cls.section) out += ` - ${cls.section}`;
    return out;
  };

  const handlePrint = () => {
    window.print();
  };

  // Convert numbers to basic words for Indian Rupees
  const amountInWords = (num) => {
    if (!num) return "Zero";
    const a = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const b = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    function inWords(n) {
      if (n === 0) return "";
      if (n < 20) return a[n];
      if (n < 100) return b[Math.floor(n / 10)] + (n % 10 !== 0 ? " " + a[n % 10] : "");
      if (n < 1000)
        return (
          a[Math.floor(n / 100)] +
          " Hundred" +
          (n % 100 !== 0 ? " and " + inWords(n % 100) : "")
        );
      if (n < 100000)
        return (
          inWords(Math.floor(n / 1000)) +
          " Thousand" +
          (n % 1000 !== 0 ? " " + inWords(n % 1000) : "")
        );
      if (n < 10000000)
        return (
          inWords(Math.floor(n / 100000)) +
          " Lakh" +
          (n % 100000 !== 0 ? " " + inWords(n % 100000) : "")
        );
      return (
        inWords(Math.floor(n / 10000000)) +
        " Crore" +
        (n % 10000000 !== 0 ? " " + inWords(n % 10000000) : "")
      );
    }

    return inWords(Math.floor(num));
  };
</script>

<style>
  @media print {
    body * {
      visibility: hidden;
    }
    #printable-receipt,
    #printable-receipt * {
      visibility: visible;
    }
    #printable-receipt {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      margin: 0;
      padding: 20px;
    }
    .no-print {
      display: none !important;
    }
  }
</style>
